import { readBoardingAccommodationTotals } from "@/utils/boardingDiscount";
import type {
  BillableItem,
  BillingStatus,
  BoardingDetail,
  BoardingPermissions,
  BoardingRecordListItem,
  BoardingRecordStatus,
  BoardingOccupancyStatus,
  BoardingOccupant,
  BoardingOccupantStatus,
  BoardingPaymentRow,
  BoardingUnitSummary,
  CaseSheetDetail,
  CaseSheetListItem,
  DetailSection,
  HealthcareGuardianSummary,
  HealthcarePetSummary,
  HealthcarePriority,
  HealthcareStatus,
  StartVisitResult,
  VisitBillingState,
  VisitDetail,
  VisitHistoryItem,
  VisitLabRecord,
  VisitListItem,
  VisitOrderItem,
  VisitRadiologyRecord,
  VisitServiceRecord,
} from "@/type/healthcare";
import { asBoardingRecordId, asRoomId } from "@/type/healthcare";
import { redactGuardianPhone } from "@/helper/permissionHelper";

function getValueByPath(source: any, path: string) {
  if (
    source
    && typeof source === "object"
    && path in source
  )
    return source[path];

  return path
    .split(".")
    .reduce((current, key) => current?.[key], source);
}

export function pickValue<T = unknown>(
  source: any,
  paths: string[],
  fallback?: T,
): T | undefined {
  for (const path of paths) {
    const value = getValueByPath(source, path);
    if (value !== undefined && value !== null && value !== "")
      return value as T;
  }

  return fallback;
}

export function toStringValue(source: any, paths: string[], fallback = "") {
  const value = pickValue<unknown>(source, paths, fallback);

  if (value === undefined || value === null)
    return fallback;

  return String(value);
}

export function toNullableString(source: any, paths: string[]) {
  const value = pickValue<unknown>(source, paths);

  if (value === undefined || value === null || value === "")
    return null;

  return String(value);
}

function toNullableBoolean(source: any, paths: string[]) {
  const value = pickValue<unknown>(source, paths);

  if (value === undefined || value === null || value === "")
    return null;
  if (typeof value === "boolean")
    return value;

  return Number(value) === 1 || String(value).toLowerCase() === "true";
}

function normalizeBoardingRecordStatus(
  value: string | null | undefined,
): BoardingRecordStatus | null {
  if (!value)
    return null;

  const normalized = value.trim().toLowerCase();

  if (!normalized)
    return null;

  if (normalized.includes("reserved"))
    return "Reserved";

  if (
    normalized.includes("checked in")
    || normalized.includes("check in")
    || normalized.includes("occupied")
    || normalized.includes("in stay")
  )
    return "Checked In";

  if (
    normalized.includes("checked out")
    || normalized.includes("check out")
    || normalized.includes("closed")
    || normalized.includes("completed")
    || normalized.includes("finished")
  )
    return "Checked Out";

  if (normalized.includes("cancel"))
    return "Cancelled";

  return value as BoardingRecordStatus;
}

function normalizeBoardingOccupancyStatus(
  value: string | null | undefined,
  recordStatus?: string | null,
): BoardingOccupancyStatus {
  const normalized = value?.trim().toLowerCase() || "";

  if (
    normalized === "available"
    || normalized.includes("vacant")
    || normalized.includes("empty")
    || normalized.includes("ready")
  )
    return "Available";

  if (normalized.includes("reserved"))
    return "Reserved";

  if (
    normalized.includes("occupied")
    || normalized.includes("checked in")
    || normalized.includes("check in")
    || normalized.includes("in stay")
    || normalized.includes("in house")
  )
    return "Occupied";

  if (normalized.includes("attention"))
    return "Attention";

  if (
    normalized.includes("checked out")
    || normalized.includes("check out")
    || normalized.includes("closed")
    || normalized.includes("completed")
    || normalized.includes("cancel")
  )
    return "Available";

  const normalizedRecordStatus = normalizeBoardingRecordStatus(recordStatus);

  if (normalizedRecordStatus === "Reserved")
    return "Reserved";

  if (normalizedRecordStatus === "Checked In")
    return "Occupied";

  if (
    normalizedRecordStatus === "Checked Out"
    || normalizedRecordStatus === "Cancelled"
  )
    return "Available";

  return "Available";
}

export function toNumberValue(
  source: any,
  paths: string[],
  fallback?: number | null,
): number | null | undefined {
  const value = pickValue<unknown>(source, paths, fallback);

  if (value === undefined || value === null || value === "")
    return fallback;

  const normalized = Number(value);

  return Number.isFinite(normalized) ? normalized : fallback;
}

/**
 * Elapsed boarding duration in whole started hours — metadata only, NOT the billing unit.
 *
 * Boarding is billed PER DAY (a day = 24h of elapsed time, rounded up: 25h → 2 days). The invoice
 * quantity is `stay_days`, which the backend computes, and that is what every UI surface displays.
 * `stay_hours` still ships and is mapped here for completeness, but nothing surfaces it — keeping it
 * out of the UI is deliberate so no screen can say "hours" next to a per-day invoice.
 */
export function toStayHours(source: any): number | null {
  const hours = toNumberValue(source, ["stay_hours"], null);
  return hours != null ? Math.ceil(hours) : null;
}

export function normalizeMethodPayload<T = any>(response: any): T {
  return (
    response?.data ??
    response?.message?.data ??
    response?.message ??
    response
  ) as T;
}

export function normalizeMethodListResponse<T = any>(response: any) {
  const payload = normalizeMethodPayload<any>(response);

  if (Array.isArray(payload))
    return { data: payload as T[], total: payload.length };

  if (Array.isArray(payload?.data))
    return {
      data: payload.data as T[],
      total:
        toNumberValue(payload, ["total", "count", "length"], payload.data.length) ??
        payload.data.length,
    };

  // The standard envelope (#16) wraps raw lists as `data.items` / `data.result`.
  // The response interceptor collapses `{ message: { ok, data } }` to
  // `{ message: <data> }`, so for a list `payload` is `{ items | result: [...] }`.
  if (Array.isArray(payload?.items))
    return {
      data: payload.items as T[],
      total: toNumberValue(payload, ["total", "count"], payload.items.length) ?? payload.items.length,
    };

  if (Array.isArray(payload?.result))
    return {
      data: payload.result as T[],
      total: toNumberValue(payload, ["total", "count"], payload.result.length) ?? payload.result.length,
    };

  if (Array.isArray(response?.message?.data))
    return {
      data: response.message.data as T[],
      total:
        toNumberValue(response, ["message.total", "message.count"], response.message.data.length) ??
        response.message.data.length,
    };

  return { data: [] as T[], total: 0 };
}

function buildDetailSection(title: string, entries: Array<[string, unknown]>): DetailSection {
  const fields = entries
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([label, value]) => ({ label, value }));

  return {
    title,
    fields,
  };
}

export function mapPetSummary(raw: any): HealthcarePetSummary {
  return {
    id: toStringValue(raw, [
      "pet.id",
      "pet.name",
      "animal_patient",
      "animal_patient.name",
      "pet_id",
      "pet_id.name",
      "custom_pet_id",
      "pet.name",
      "name",
    ]),
    name: toStringValue(raw, [
      "pet.name",
      "pet.pet_name",
      "animal_patient.pet_name",
      "pet_id.pet_name",
      "pet_name",
      "custom_pet_name",
      "pet_label",
    ]),
    image: toNullableString(raw, [
      "pet.image",
      "pet.pet_image",
      "animal_patient.pet_image",
      "pet_id.pet_image",
      "pet_image",
      "custom_pet_image",
      "image",
    ]),
    species: toNullableString(raw, ["pet.species", "animal_patient.animal_species", "pet_id.animal_species", "animal_species", "pet_species"]),
    animalType: toNullableString(raw, ["pet.animal_type", "animal_patient.animal_type", "pet_id.animal_type", "animal_type", "pet_animal_type"]),
    breed: toNullableString(raw, ["pet.breed", "animal_patient.breed", "pet_id.breed", "breed", "pet_breed"]),
    gender: toNullableString(raw, ["pet.gender", "animal_patient.gender", "pet_id.gender", "gender", "pet_gender"]),
    age: toNullableString(raw, ["pet.age", "age", "pet_age"]),
    weight: pickValue(raw, ["pet.weight", "pet_id.weight", "weight", "pet_weight"], null),
  };
}

export function mapGuardianSummary(raw: any): HealthcareGuardianSummary {
  return {
    id: toStringValue(raw, [
      "guardian.id",
      "guardian.name",
      "guardian",
      "guardian_id",
      "guardian_id",
      "guardian_id.name",
      "guardian_id.name",
      "custom_guardian_id",
      "customer_id",
      "customer.name",
      // Plain summary shape (already-mapped guardian object, e.g. from mocks/nested).
      "id",
      "name",
    ]),
    customerId: toNullableString(raw, [
      "guardian.customer_id",
      "guardian_id.customer_id",
      "guardian_id.customer_id",
      "customer_id",
      "customer.name",
    ]),
    name: toStringValue(raw, [
      "guardian_name",
      "guardian_name",
      "guardian.full_name",
      "guardian_id.full_name",
      "guardian_id.full_name",
      "customer.full_name",
      "full_name",
      "customer_name",
      "owner_name",
      "guardian.name",
      "guardian",
      // Plain summary shape (already-mapped guardian object, e.g. from mocks/nested).
      "name",
    ]),
    // Display-redacted: blanks to "" when the current role is blocked from
    // guardian phone (or access is unresolved). Inert while the blocklist is
    // empty. See docs/AGENT.md "Sensitive phone visibility".
    phone: redactGuardianPhone(toNullableString(raw, [
      "guardian.phone",
      "guardian.mobile_no",
      "guardian_id.mobile_no",
      "customer.mobile_no",
      "phone",
      "phone_number",
      "mobile_no",
      "contact_number",
    ])),
    image: toNullableString(raw, [
      "guardian.image",
      "guardian.guardian_image",
      "guardian_id.guardian_image",
      "guardian_id.guardian_image",
      "customer.image",
      "customer.customer_image",
      "guardian_image",
      "guardian_image",
      "customer_image",
    ]),
    email: toNullableString(raw, ["guardian.email", "email", "email_id", "guardian.email_id"]),
  };
}

export function mapCaseSheetListItem(raw: any): CaseSheetListItem {
  return {
    id: toStringValue(raw, ["name", "case_sheet_id", "id"]),
    caseSheetNo: toStringValue(raw, ["case_sheet_no", "case_sheet_number", "name"]),
    pet: mapPetSummary({
      ...raw,
      pet: pickValue(raw, ["pet"], raw?.pet),
    }),
    guardian: mapGuardianSummary({
      ...raw,
      guardian: pickValue(raw, ["guardian", "customer"], raw?.guardian),
    }),
    chiefComplaint: toNullableString(raw, [
      "chief_complaint",
      "complaint",
      "intake_reason",
      "reason_for_visit",
    ]),
    intakeSummary: toNullableString(raw, [
      "intake_summary",
      "intake_notes",
      "summary",
      "background",
    ]),
    priority: toNullableString(raw, ["priority", "triage_priority"]) as HealthcarePriority | null,
    status: toNullableString(raw, ["status", "workflow_state"]) as HealthcareStatus | null,
    billingStatus: toNullableString(raw, [
      "billing_status",
      "invoice_status",
      "payment_status",
    ]) as BillingStatus | null,
    createdAt: toNullableString(raw, [
      "created_at",
      "creation",
      "appointment_time",
      "modified",
    ]),
    linkedVisitId: toNullableString(raw, [
      "linked_visit_id",
      "custom_linked_visit_id",
      "visit_id",
      "vet_visit",
      "visit.name",
      "linked_visit.name",
    ]),
    linkedVisitStatus: toNullableString(raw, [
      "linked_visit_status",
      "visit_status",
      "visit.status",
    ]) as HealthcareStatus | null,
  };
}

export function mapCaseSheetDetail(raw: any): CaseSheetDetail {
  const base = mapCaseSheetListItem(raw);
  const checked = (path: string) => Boolean(Number(pickValue(raw, [path], 0) ?? 0));
  const symptoms = toNullableString(raw, ["symptoms", "presenting_symptoms"]);
  const background = toNullableString(raw, [
    "background",
    "background_notes",
    "medical_background",
  ]);
  const history = toNullableString(raw, ["history", "medical_history", "pet_history"]);
  const triageNotes = toNullableString(raw, ["triage_notes", "triage", "nursing_notes"]);
  const intakeNotes = toNullableString(raw, ["intake_notes", "notes"]);
  const symptomDuration = toNullableString(raw, ["symptom_duration"]);
  const complaintOther = toNullableString(raw, ["complaint_other"]);
  const previousVetVisit = checked("previous_vet_visit");
  const chronicDisease = checked("chronic_disease");
  const currentlyOnMedication = checked("currently_on_medication");
  const contactWithOtherAnimals = checked("contact_with_other_animals");
  const sections = [
    buildDetailSection("Intake", [
      ["Case Sheet", base.caseSheetNo],
      ["Chief Complaint", base.chiefComplaint],
      ["Complaint Details", complaintOther],
      ["Symptom Duration", symptomDuration],
      ["Intake Summary", base.intakeSummary],
      ["Intake Notes", intakeNotes],
      ["Priority", base.priority],
      ["Status", base.status],
      ["Created", base.createdAt],
      ["Billing", base.billingStatus],
    ]),
    buildDetailSection("Pet", [
      ["Pet", base.pet.name],
      ["Species", base.pet.species],
      ["Breed", base.pet.breed],
      ["Gender", base.pet.gender],
      ["Age", base.pet.age],
      ["Weight", base.pet.weight],
    ]),
    buildDetailSection("Owner", [
      ["Owner", base.guardian.name],
      ["Phone", base.guardian.phone],
      ["Email", base.guardian.email],
    ]),
    buildDetailSection("Clinical Background", [
      ["Symptoms", symptoms],
      ["Background", background],
      ["History", history],
      ["Triage Notes", triageNotes],
      ["Vaccination Status", toNullableString(raw, ["vaccination_status"])],
      ["Last Vaccine Date", toNullableString(raw, ["last_vaccine_date"])],
      ["Neutered / Spayed", toNullableString(raw, ["neutered_spayed"])],
      ["Previous Vet Visit", previousVetVisit ? "Yes" : null],
      ["Previous Vet Details", toNullableString(raw, ["previous_vet_details"])],
      ["Chronic Disease", chronicDisease ? "Yes" : null],
      ["Chronic Disease Details", toNullableString(raw, ["chronic_disease_details"])],
      ["Currently on Medication", currentlyOnMedication ? "Yes" : null],
      ["Medication Details", toNullableString(raw, ["medication_details"])],
    ]),
    buildDetailSection("Daily Condition", [
      ["Appetite", toNullableString(raw, ["appetite_status"])],
      ["Water Intake", toNullableString(raw, ["water_intake_status"])],
      ["Urination", toNullableString(raw, ["urination_status"])],
      ["Defecation", toNullableString(raw, ["defecation_status"])],
      ["Activity", toNullableString(raw, ["activity_status"])],
    ]),
    buildDetailSection("Lifestyle", [
      ["Feeding Type", toNullableString(raw, ["feeding_type"])],
      ["Feeding Type Other", toNullableString(raw, ["feeding_type_other"])],
      ["Housing / Living Environment", toNullableString(raw, ["housing_environment"])],
      ["Contact With Other Animals", contactWithOtherAnimals ? "Yes" : null],
    ]),
  ].filter(section => section.fields.length > 0);

  return {
    ...base,
    symptomDuration,
    complaintOther,
    symptoms,
    background,
    history,
    triageNotes,
    intakeNotes,
    hasVomiting: checked("has_vomiting"),
    hasDiarrhea: checked("has_diarrhea"),
    hasCough: checked("has_cough"),
    hasSneezing: checked("has_sneezing"),
    hasLossOfAppetite: checked("has_loss_of_appetite"),
    hasLethargy: checked("has_lethargy"),
    hasItching: checked("has_itching"),
    hasWound: checked("has_wound"),
    hasLimping: checked("has_limping"),
    hasBreathingIssue: checked("has_breathing_issue"),
    hasEyeDischarge: checked("has_eye_discharge"),
    hasEarDischarge: checked("has_ear_discharge"),
    hasFeverFlag: checked("has_fever_flag"),
    vaccinationStatus: toNullableString(raw, ["vaccination_status"]),
    lastVaccineDate: toNullableString(raw, ["last_vaccine_date"]),
    neuteredSpayed: toNullableString(raw, ["neutered_spayed"]),
    previousVetVisit,
    previousVetDetails: toNullableString(raw, ["previous_vet_details"]),
    chronicDisease,
    chronicDiseaseDetails: toNullableString(raw, ["chronic_disease_details"]),
    currentlyOnMedication,
    medicationDetails: toNullableString(raw, ["medication_details"]),
    appetiteStatus: toNullableString(raw, ["appetite_status"]),
    waterIntakeStatus: toNullableString(raw, ["water_intake_status"]),
    urinationStatus: toNullableString(raw, ["urination_status"]),
    defecationStatus: toNullableString(raw, ["defecation_status"]),
    activityStatus: toNullableString(raw, ["activity_status"]),
    feedingType: toNullableString(raw, ["feeding_type"]),
    feedingTypeOther: toNullableString(raw, ["feeding_type_other"]),
    housingEnvironment: toNullableString(raw, ["housing_environment"]),
    contactWithOtherAnimals,
    requestedBy: toNullableString(raw, ["requested_by", "owner_name", "guardian_name"]),
    practitionerName: toNullableString(raw, ["practitioner_name", "doctor_name"]),
    sections,
  };
}

export function mapStartVisitResult(raw: any, caseSheetId: string): StartVisitResult | null {
  const visitId = toNullableString(raw, [
    "visit_id",
    "name",
    "visit.name",
    "message.visit_id",
  ]);

  if (!visitId)
    return null;

  return {
    visitId,
    caseSheetId,
    created: Boolean(
      pickValue(raw, ["created", "is_new", "message.created"], false),
    ),
  };
}

export function mapVisitHistoryItem(raw: any): VisitHistoryItem {
  return {
    id: toStringValue(raw, ["name", "id"]),
    title: toStringValue(raw, ["title", "encounter_comment", "case_sheet_no", "name"]),
    subtitle: toNullableString(raw, ["subtitle", "status", "practitioner"]),
    // "visit_datetime"/"creation" are the real Vet Visit resource fields; the
    // earlier keys are the legacy Patient Encounter names. Without them the real
    // backend leaves the history date blank (mock populated it from rich objects).
    date: toNullableString(raw, ["date", "encounter_date", "visit_datetime", "created_at", "creation"]),
    time: toNullableString(raw, ["time", "encounter_time"]),
    // ⚠️ NAMES ONLY — never fall back to the raw `doctor` link id. That fallback surfaced
    // "HCP-00012" to users wherever `doctor_name` was blank on the row. Frappe returns dotted
    // link fields as FLAT keys ("doctor.practitioner_name"), so both spellings are probed.
    // If no human name is available, return null and let the UI omit the practitioner line
    // rather than print an internal id.
    practitioner: toNullableString(raw, [
      "practitioner_name",
      "doctor_name",
      "doctor.practitioner_name",
      "doctor.doctor_name",
      "doctor.full_name",
    ]),
    // "case_summary" is the Vet Visit chief-complaint field; without it the real
    // backend returns no complaint line for prior visits (mock used chiefComplaint).
    note: toNullableString(raw, ["note", "encounter_comment", "case_summary", "summary"]),
    priority: toNullableString(raw, ["priority", "triage_priority"]) as HealthcarePriority | null,
    billingStatus: toNullableString(raw, [
      "billing_status",
      "payment_status",
      "billing.status",
    ]) as BillingStatus | null,
    orderCount: toNumberValue(raw, ["order_count", "orders_count", "orders.length"], null) ?? null,
    // Legacy `assessment` chain, left intact for the older visit-workbench surfaces that still
    // read `item.assessment`. The visit-v11 history surfaces read `diagnosis` (below) instead.
    assessment: toNullableString(raw, ["assessment", "diagnosis", "clinical_assessment"]),
    // Primary diagnosis read DIRECTLY from the Vet Visit `diagnosis` field — NOT via the
    // assessment chain, whose multi-key fallback masked the field going unfetched. HTML
    // (Text Editor); every consumer strips tags (plainText) before rendering, never v-html.
    diagnosis: toNullableString(raw, ["diagnosis"]),
    plan: toNullableString(raw, ["plan", "treatment_plan"]),
    total: toNumberValue(raw, ["total", "billing.total", "grand_total"], null) ?? null,
    currency: toNullableString(raw, ["currency", "billing.currency"]),
  };
}

export function mapVisitOrder(raw: any): VisitOrderItem {
  const kind = toStringValue(raw, ["kind", "type", "order_type"], "other");

  return {
    id: toNullableString(raw, ["name", "id"]) ?? undefined,
    title: toStringValue(raw, ["title", "item_name", "service_name", "name"]),
    kind: (["medication", "service", "lab", "radiology", "procedure", "other"].includes(kind)
      ? kind
      : "other") as VisitOrderItem["kind"],
    status: toNullableString(raw, ["status", "order_status"]),
    qty: toNumberValue(raw, ["qty", "quantity"], null) ?? null,
    price: toNumberValue(raw, ["price", "rate", "amount"], null) ?? null,
    note: toNullableString(raw, ["note", "description"]),
    linkedServiceId: toNullableString(raw, ["linked_service_id", "service_id"]),
    itemCode: toNullableString(raw, ["item_code", "service_code", "code"]),
    templateId: toNullableString(raw, ["template_id", "care_service_id"]),
    category: toNullableString(raw, ["category", "item_group", "department"]),
    priority: toNullableString(raw, ["priority", "urgency"]),
    definitionId: toNullableString(raw, ["definition_id", "catalog_id", "template_id", "care_service_id"]),
    specimen: toNullableString(raw, ["specimen"]),
    modality: toNullableString(raw, ["modality"]),
    bodyPart: toNullableString(raw, ["body_part", "bodyPart"]),
    orderId: toNullableString(raw, ["order_id", "orderId"]),
    linkedDoctype: toNullableString(raw, ["linked_doctype", "linkedDoctype"]),
    linkedName: toNullableString(raw, ["linked_name", "linkedName"]),
    medication: toNullableString(raw, ["medication"]),
    linkedItem: toNullableString(raw, ["linked_item", "linkedItem"]),
    warehouse: toNullableString(raw, ["warehouse"]),
    dose: toNullableString(raw, ["dose", "dosage"]),
    dosageFormOrUnit: toNullableString(raw, ["dosage_form_or_unit", "dosageFormOrUnit", "unit"]),
    route: toNullableString(raw, ["route"]),
    frequency: toNullableString(raw, ["frequency"]),
    durationDays: toNumberValue(raw, ["duration_days", "durationDays"], null) ?? null,
    quantity: toNumberValue(raw, ["quantity"], null) ?? null,
    unit: toNullableString(raw, ["unit"]),
    instructions: toNullableString(raw, ["instructions"]),
    billable: raw?.billable === undefined || raw?.billable === null ? null : Boolean(raw.billable),
    rate: toNumberValue(raw, ["rate"], null) ?? null,
    procedureTemplate: toNullableString(raw, ["procedure_template", "procedureTemplate"]),
    careService: toNullableString(raw, ["care_service", "careService", "care_service_id"]),
    scheduledAt: toNullableString(raw, ["scheduled_at", "scheduledAt"]),
    provider: toNullableString(raw, ["provider"]),
    dueDate: toNullableString(raw, ["due_date", "dueDate"]),
    indication: toNullableString(raw, ["indication"]),
  };
}

export function mapVisitLabRecord(raw: any): VisitLabRecord {
  return {
    id: toStringValue(raw, ["name", "id"]),
    visitId: toStringValue(raw, ["visit", "visit_id", "visitId", "parent"]),
    orderId: toNullableString(raw, ["order_id", "orderId"]),
    definitionId: toNullableString(raw, ["definition_id", "definitionId", "template_id", "care_service"]),
    careServiceId: toNullableString(raw, ["care_service", "careService", "template_id"]),
    careServiceName: toNullableString(raw, ["care_service.service_name", "care_service_label", "care_service_name"]),
    testName: toStringValue(raw, ["test_name", "testName", "care_service.service_name", "service_name", "care_service_label", "title", "name"]),
    code: toNullableString(raw, ["code", "item_code", "itemCode", "template_id", "care_service"]),
    rate: toNumberValue(raw, ["rate", "price", "default_price"], null) ?? null,
    petId: toNullableString(raw, ["pet", "pet_id"]),
    petName: toNullableString(raw, ["pet.pet_name", "pet_name", "pet_label"]),
    petGender: toNullableString(raw, ["pet.gender", "gender", "pet_gender"]),
    petImage: toNullableString(raw, ["pet.pet_image", "pet_image", "pet_id.pet_image"]),
    petType: toNullableString(raw, ["pet.animal_type", "animal_type", "pet_animal_type"]),
    petSpecies: toNullableString(raw, ["pet.animal_species", "animal_species", "pet_animal_species"]),
    petBreed: toNullableString(raw, ["pet.breed", "breed", "pet_breed"]),
    petBirthDate: toNullableString(raw, ["pet.birth_date", "birth_date", "pet_birth_date"]),
    guardianId: toNullableString(raw, ["guardian", "guardian_id"]),
    guardianName: toNullableString(raw, ["guardian_name", "guardian_full_name"]),
    doctor: toNullableString(raw, ["doctor"]),
    doctorName: toNullableString(raw, ["doctor.practitioner_name", "doctor_name", "practitioner_name"]),
    specimen: toNullableString(raw, ["specimen"]),
    status: toStringValue(raw, ["status"], "Ordered"),
    priority: toNullableString(raw, ["priority", "urgency"]),
    result: toNullableString(raw, ["result", "results", "lab_result"]),
    extractedResult: toNullableString(raw, ["extracted_result"]),
    resultFile: toNullableString(raw, ["result_file", "resultFile", "file_url", "attachment"]),
    sampleCollectedBy: toNullableString(raw, ["sample_collected_by", "sampleCollectedBy"]),
    sampleCollectedAt: toNullableString(raw, ["sample_collected_at", "sampleCollectedAt"]),
    resultEnteredBy: toNullableString(raw, ["result_entered_by", "resultEnteredBy"]),
    resultEnteredAt: toNullableString(raw, ["result_entered_at", "resultEnteredAt"]),
    releasedBy: toNullableString(raw, ["released_by", "releasedBy"]),
    modifiedBy: toNullableString(raw, ["modified_by", "modifiedBy"]),
    resultVisibility: toNullableString(raw, ["result_visibility", "resultVisibility"]),
    attachmentRequired: toNullableBoolean(raw, ["attachment_required", "attachmentRequired"]),
    // `practitioner_reviewed` was a false signal — that string does not exist anywhere in
    // pet_app. The real column on BOTH Lab and Imaging is doctor_reviewed; both mappers now match.
    doctorReviewed: toNullableBoolean(raw, ["doctor_reviewed", "doctorReviewed"]),
    doctorReviewedAt: toNullableString(raw, ["doctor_reviewed_at", "doctorReviewedAt"]),
    // WHEN this lab is due. NO fallback chain — `Lab` has no `due_date` and no `scheduled_at`,
    // so unlike the service and procedure mappers there is nothing else to fall back TO. If the
    // column is absent from the response this is null, and the row reads as unscheduled, which
    // is the honest answer rather than borrowing `creation` (as `orderedAt` deliberately does)
    // and presenting an order's age as its due time.
    scheduledDatetime: toNullableString(raw, ["scheduled_datetime", "scheduledDatetime"]),
    orderedAt: toNullableString(raw, ["ordered_at", "orderedAt", "creation"]),
    startAt: toNullableString(raw, ["start_at", "startAt"]),
    endAt: toNullableString(raw, ["end_at", "endAt"]),
    releasedAt: toNullableString(raw, ["released_at", "releasedAt"]),
    attachments: Array.isArray(raw?.attachments) ? raw.attachments : [],
  };
}

export function mapVisitRadiologyRecord(raw: any): VisitRadiologyRecord {
  return {
    id: toStringValue(raw, ["name", "id"]),
    visitId: toStringValue(raw, ["visit", "visit_id", "visitId", "parent"]),
    orderId: toNullableString(raw, ["order_id", "orderId"]),
    definitionId: toNullableString(raw, ["definition_id", "definitionId", "template_id", "care_service"]),
    careServiceId: toNullableString(raw, ["care_service", "careService", "template_id"]),
    careServiceName: toNullableString(raw, ["care_service_label", "care_service.service_name", "care_service_name"]),
    studyName: toStringValue(raw, ["study_name", "studyName", "service_name", "care_service_label", "title", "name"]),
    code: toNullableString(raw, ["code", "item_code", "itemCode", "template_id", "care_service"]),
    rate: toNumberValue(raw, ["rate", "price", "default_price"], null) ?? null,
    petId: toNullableString(raw, ["pet", "pet_id"]),
    petName: toNullableString(raw, ["pet.pet_name", "pet_name", "pet_label"]),
    petGender: toNullableString(raw, ["pet.gender", "gender", "pet_gender"]),
    petImage: toNullableString(raw, ["pet.pet_image", "pet_image", "pet_id.pet_image"]),
    petType: toNullableString(raw, ["pet.animal_type", "animal_type", "pet_animal_type"]),
    petSpecies: toNullableString(raw, ["pet.animal_species", "animal_species", "pet_animal_species"]),
    petBreed: toNullableString(raw, ["pet.breed", "breed", "pet_breed"]),
    petBirthDate: toNullableString(raw, ["pet.birth_date", "birth_date", "pet_birth_date"]),
    guardianId: toNullableString(raw, ["guardian", "guardian_id"]),
    guardianName: toNullableString(raw, ["guardian_name", "guardian_full_name"]),
    doctor: toNullableString(raw, ["doctor"]),
    doctorName: toNullableString(raw, ["doctor.practitioner_name", "doctor_name", "practitioner_name"]),
    modality: toNullableString(raw, ["modality"]),
    bodyPart: toNullableString(raw, ["body_part", "bodyPart"]),
    note: toNullableString(raw, ["note", "order_note", "remarks", "description"]),
    status: toStringValue(raw, ["status"], "Ordered"),
    priority: toNullableString(raw, ["priority", "urgency"]),
    report: toNullableString(raw, ["report", "radiology_report"]),
    image: toNullableString(raw, ["image", "radiology_image", "scan_image", "file_url"]),
    reportFile: toNullableString(raw, ["report_file", "reportFile", "attachment"]),
    resultEnteredBy: toNullableString(raw, ["result_entered_by", "resultEnteredBy"]),
    resultEnteredAt: toNullableString(raw, ["result_entered_at", "resultEnteredAt"]),
    releasedBy: toNullableString(raw, ["released_by", "releasedBy"]),
    modifiedBy: toNullableString(raw, ["modified_by", "modifiedBy"]),
    releasedAt: toNullableString(raw, ["released_at", "releasedAt"]),
    resultVisibility: toNullableString(raw, ["result_visibility", "resultVisibility"]),
    attachmentRequired: toNullableBoolean(raw, ["attachment_required", "attachmentRequired"]),
    doctorReviewed: toNullableBoolean(raw, ["doctor_reviewed", "doctorReviewed"]),
    doctorReviewedAt: toNullableString(raw, ["doctor_reviewed_at", "doctorReviewedAt"]),
    // WHEN this study is due. Same as the Lab above: no fallback exists, because `Imaging`
    // carries no other "when" column.
    scheduledDatetime: toNullableString(raw, ["scheduled_datetime", "scheduledDatetime"]),
    orderedAt: toNullableString(raw, ["ordered_at", "orderedAt", "creation"]),
    attachments: Array.isArray(raw?.attachments) ? raw.attachments : [],
  };
}

export function mapVisitServiceRecord(raw: any): VisitServiceRecord {
  return {
    id: toStringValue(raw, ["name", "id"]),
    visitId: toStringValue(raw, ["visit", "visit_id", "visitId", "parent"]),
    orderId: toNullableString(raw, ["order_id", "orderId"]),
    definitionId: toNullableString(raw, ["definition_id", "definitionId", "template_id", "procedure_template"]),
    procedureTemplate: toNullableString(raw, ["procedure_template", "procedureTemplate"]),
    careServiceId: toNullableString(raw, ["care_service", "careService", "care_service_id"]),
    careServiceName: toNullableString(raw, ["care_service_label", "care_service.service_name", "care_service_name"]),
    serviceName: toStringValue(raw, ["pet_service_name", "service_name", "serviceName", "procedure_name", "procedure_template_label", "procedureTemplate", "procedure_template", "title", "name"]),
    code: toNullableString(raw, ["code", "item_code", "itemCode", "template_id", "procedure_template", "care_service_id", "care_service"]),
    rate: toNumberValue(raw, ["rate", "price", "amount", "default_price"], null) ?? null,
    petId: toNullableString(raw, ["pet", "pet_id"]),
    // Read defensively for the workspace payload (which DOES carry a joined name) and left
    // null on a plain list read, where `enrichProcedureRecordLabels` fills it in.
    petName: toNullableString(raw, ["pet_name", "petName", "pet.pet_name", "pet_id.pet_name"]),
    petImage: toNullableString(raw, ["pet_image", "petImage", "pet.pet_image", "pet_id.pet_image"]),
    guardianId: toNullableString(raw, ["guardian", "guardian_id"]),
    guardianName: toNullableString(raw, [
      "guardian_id.full_name",
      "guardian_full_name",
      "full_name",
      "guardian_name",
      "guardianName",
    ]) ?? null,
    doctor: toNullableString(raw, ["doctor"]),
    doctorName: toNullableString(raw, ["doctor_name", "doctorName", "doctor.practitioner_name", "doctor.doctor_name"]),
    provider: toNullableString(raw, ["provider"]),
    providerName: toNullableString(raw, ["provider_name", "providerName", "provider.practitioner_name"]),
    status: toStringValue(raw, ["status"], "Ordered"),
    priority: toNullableString(raw, ["priority", "urgency"]),
    assignedTo: toNullableString(raw, ["assigned_to", "assignedTo", "provider", "practitioner"]),
    note: toNullableString(raw, ["note", "description", "procedure_note"]),
    price: toNumberValue(raw, ["price", "rate", "amount", "default_price"], null) ?? null,
    performedAt: toNullableString(raw, ["performed_at", "performedAt", "completed_at", "scheduled_at", "due_date"]),
    scheduledAt: toNullableString(raw, ["scheduled_at", "scheduledAt"]),
    // WHEN this is due. `scheduled_at` IS a fallback here, unlike on lab/imaging: the backend
    // syncs the two, so on a procedure they hold the same moment and an older row written before
    // the sync existed still answers correctly through the second name.
    scheduledDatetime: toNullableString(raw, ["scheduled_datetime", "scheduledDatetime", "scheduled_at", "scheduledAt"]),
    startedAt: toNullableString(raw, ["started_at", "startedAt"]),
    completedAt: toNullableString(raw, ["completed_at", "completedAt"]),
    closedAt: toNullableString(raw, ["closed_at", "closedAt"]),
    indication: toNullableString(raw, ["indication"]),
    consentObtained: toNullableBoolean(raw, ["consent_obtained", "consentObtained"]),
    anesthesiaUsed: toNullableBoolean(raw, ["anesthesia_used", "anesthesiaUsed"]),
    procedureNote: toNullableString(raw, ["procedure_note", "procedureNote", "note"]),
    findings: toNullableString(raw, ["findings"]),
    outcome: toNullableString(raw, ["outcome"]),
    complications: toNullableString(raw, ["complications"]),
    aftercareInstructions: toNullableString(raw, ["aftercare_instructions", "aftercareInstructions"]),
    rating: toNumberValue(raw, ["rating", "admin_rating", "adminRating"], null) ?? null,
    adminRating: toNumberValue(raw, ["admin_rating", "adminRating", "rating"], null) ?? null,
    adminRatingNote: toNullableString(raw, ["admin_rating_note", "adminRatingNote", "rating_note"]),
    deathRecord: toNullableString(raw, ["death_record", "deathRecord", "linked_death_record"]),
    deathRecordStatus: toNullableString(raw, ["death_record_status", "deathRecordStatus"]),
    checklist: Array.isArray(raw?.checklist) ? raw.checklist : [],
    attachments: Array.isArray(raw?.attachments) ? raw.attachments : [],
    notes: Array.isArray(raw?.notes) ? raw.notes : [],
  };
}

export function mapBillableItem(raw: any): BillableItem {
  const qty = toNumberValue(raw, ["qty", "quantity"], null) ?? null;
  const rate = toNumberValue(raw, ["rate", "price", "amount"], null) ?? null;
  const explicitAmount = toNumberValue(raw, ["amount", "line_total"], null);

  return {
    id: toNullableString(raw, ["name", "id"]) ?? toStringValue(raw, ["item_code", "service_code"], ""),
    itemName: toStringValue(raw, [
      "item_name",
      "service_name",
      "title",
      "name",
    ]),
    itemCode: toNullableString(raw, ["item_code", "service_code", "code"]),
    itemType: toNullableString(raw, ["item_type", "kind", "type"]),
    qty,
    rate,
    // Backend-owned. When the server sends no amount we do NOT invent one: `qty * rate` is a
    // guess, and it used to be sent straight back as truth (docs/BOARDING_GAPS.md Tier-1 #2).
    // Null renders as "—" — an unpriced line must read as unpriced, never as free.
    amount: explicitAmount ?? null,
    status: toNullableString(raw, ["status"]),
    // The documented per-pet attribution (Part A §3.3). Was dropped here, which is why the
    // orders panel could not say whose charge any line was.
    pet: toNullableString(raw, ["pet", "pet_id"]),
    petName: toNullableString(raw, ["pet_name", "petName"]),
    note: toNullableString(raw, ["note", "description"]),
    linkedServiceId: toNullableString(raw, ["linked_service_id", "linkedServiceId", "service_id"]),
    linkedDoctype: toNullableString(raw, [
      "linked_doctype",
      "linkedDoctype",
      "source_doctype",
      "sourceDoctype",
      "reference_doctype",
      "referenceDoctype",
      "doctype",
    ]),
    linkedName: toNullableString(raw, [
      "linked_name",
      "linkedName",
      "linked_record",
      "linkedRecord",
      "order_id",
      "orderId",
      "reference_name",
      "referenceName",
      "target_name",
      "targetName",
    ]),
    sourceName: toNullableString(raw, [
      "source_name",
      "sourceName",
      "reference_name",
      "referenceName",
      "order_id",
      "orderId",
    ]),
    // The medication schedule, straight off the row the backend echoed it onto. Read back rather
    // than remembered from the request: the echo is the only proof the course was stored.
    //
    // `durationDays` is kept a STRING — the backend field is `Data`, and `toNumberValue` would
    // turn a blank into `0`, manufacturing "a course of zero days" out of "no course recorded".
    dosage: toNullableString(raw, ["dosage"]),
    frequency: toNullableString(raw, ["frequency"]),
    durationDays: toNullableString(raw, ["duration_days", "durationDays"]),
    // WHEN the order is scheduled, echoed onto the row. Same read-back discipline: the row is
    // the only proof the time was stored, so a schedule that failed to save reads as absent
    // rather than as whatever the dialog happened to send.
    scheduledDatetime: toNullableString(raw, ["scheduled_datetime", "scheduledDatetime"]),
    attachments: [
      ...(Array.isArray(raw?.attachments) ? raw.attachments : []),
      ...(Array.isArray(raw?.files) ? raw.files : []),
      ...(Array.isArray(raw?._attachments) ? raw._attachments : []),
      ...(Array.isArray(raw?.file_attachments) ? raw.file_attachments : []),
    ],
  };
}

export function mapVisitBilling(raw: any): VisitBillingState {
  const total = toNumberValue(raw, ["total", "grand_total", "custom_total_amount"], null) ?? null;
  const deposit = toNumberValue(raw, ["deposit", "advance_amount"], null) ?? null;
  const paid = toNumberValue(raw, ["paid", "paid_amount"], null) ?? null;
  const explicitBalance = toNumberValue(raw, ["balance", "balance_amount"], null);
  const balance =
    explicitBalance ??
    (total != null ? total - (paid ?? 0) - (deposit ?? 0) : null);

  return {
    status: toNullableString(raw, [
      "status",
      "billing_status",
      "payment_status",
    ]) as BillingStatus | null,
    currency: toNullableString(raw, ["currency"]),
    total,
    deposit,
    paid,
    balance,
  };
}

export function mapVisitListItem(raw: any): VisitListItem {
  return {
    id: toStringValue(raw, ["name", "visit_id", "id"]),
    caseSheetId: toNullableString(raw, ["case_sheet_id", "case_sheet", "linked_case_sheet_id"]),
    caseSheetNo: toNullableString(raw, [
      "case_sheet_no",
      "linked_case_sheet_no",
      "case_sheet_number",
    ]),
    status: toNullableString(raw, ["status", "workflow_state"]) as HealthcareStatus | null,
    priority: toNullableString(raw, ["priority"]) as HealthcarePriority | null,
    billingStatus: toNullableString(raw, [
      "billing_status",
      "payment_status",
    ]) as BillingStatus | null,
    practitionerName: toNullableString(raw, [
      "practitioner_name",
      "doctor_name",
      "doctor.doctor_name",
      "practitioner.practitioner_name",
      "doctor.practitioner_name",
      "doctor",
    ]),
    // Already fetched (`doctor` is in the list's field set) — no new column, so no new way for the
    // query to fail. Callers map it to a photo from the practitioner list they already hold.
    practitionerId: toNullableString(raw, ["doctor", "practitioner", "primary_practitioner"]),
    // Null on every current backend — see the field note on `VisitListItem.transferredFromName`.
    // Listed as an alias chain (not a single name) so whichever column the backend settles on
    // starts feeding the card without another frontend change.
    transferredFromName: toNullableString(raw, [
      "original_practitioner_name",
      "first_practitioner_name",
      "transferred_from_name",
    ]),
    visitDate: toNullableString(raw, ["visit_date", "encounter_date", "date", "visit_datetime"]),
    visitTime: toNullableString(raw, ["visit_time", "encounter_time", "time"]),
    startedAt: toNullableString(raw, ["started_at", "visit_datetime", "creation", "modified"]),
    consultationSeconds: toConsultationSeconds(raw?.consultation_seconds),
    pet: mapPetSummary(raw),
    guardian: mapGuardianSummary(raw),
  };
}

/** Finished consultation time; null when never timed (0 included — a zero-second visit was not timed). */
function toConsultationSeconds(value: unknown): number | null {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
}

export function mapVisitDetail(raw: any): VisitDetail {
  const linkedCaseSheetRaw = pickValue<any>(raw, ["linked_case_sheet", "case_sheet"], null);

  return {
    id: toStringValue(raw, ["name", "visit_id", "id"]),
    caseSheetId: toNullableString(raw, ["case_sheet_id", "case_sheet", "linked_case_sheet_id"]),
    caseSheetNo: toNullableString(raw, ["case_sheet_no", "linked_case_sheet_no"]),
    status: toNullableString(raw, ["status", "workflow_state"]) as HealthcareStatus | null,
    priority: toNullableString(raw, ["priority"]) as HealthcarePriority | null,
    practitionerId: toNullableString(raw, ["practitioner_id", "practitioner", "doctor"]),
    practitionerName: toNullableString(raw, [
      "practitioner_name",
      "doctor_name",
      "doctor.doctor_name",
      "practitioner.practitioner_name",
      "doctor.practitioner_name",
      "doctor",
    ]),
    practitionerImage: toNullableString(raw, [
      "practitioner_image",
      "doctor_image",
      "practitioner.image",
    ]),
    visitDate: toNullableString(raw, ["visit_date", "encounter_date", "visit_datetime"]),
    visitTime: toNullableString(raw, ["visit_time", "encounter_time"]),
    startedAt: toNullableString(raw, ["started_at", "visit_datetime", "creation", "modified"]),
    pet: mapPetSummary(raw),
    guardian: mapGuardianSummary(raw),
    chiefComplaint: toNullableString(raw, ["chief_complaint", "case_summary", "complaint"]),
    intakeSummary: toNullableString(raw, ["intake_summary", "encounter_comment", "summary", "case_summary"]),
    overview: toNullableString(raw, ["overview", "clinical_overview", "encounter_comment"]),
    examination: toNullableString(raw, ["examination", "examination_notes", "objective", "physical_exam"]),
    assessment: toNullableString(raw, ["assessment", "diagnosis", "clinical_assessment"]),
    plan: toNullableString(raw, ["plan", "treatment_plan"]),
    instructions: toNullableString(raw, ["instructions", "discharge_instructions"]),
    historyItems: (
      pickValue<any[]>(raw, ["history", "previous_visits", "timeline"], []) ?? []
    ).map(mapVisitHistoryItem),
    orders: (
      pickValue<any[]>(raw, ["orders", "actions", "services", "lab_requests"], []) ?? []
    ).map(mapVisitOrder),
    labRecords: (
      pickValue<any[]>(raw, ["lab_records", "labRecords", "labs"], []) ?? []
    ).map(mapVisitLabRecord),
    radiologyRecords: (
      pickValue<any[]>(raw, ["radiology_records", "radiologyRecords", "imaging"], []) ?? []
    ).map(mapVisitRadiologyRecord),
    serviceRecords: (
      pickValue<any[]>(raw, ["service_records", "serviceRecords", "visit_services"], []) ?? []
    ).map(mapVisitServiceRecord),
    billing: mapVisitBilling(pickValue(raw, ["billing"], raw)),
    linkedCaseSheet: linkedCaseSheetRaw ? mapCaseSheetDetail(linkedCaseSheetRaw) : null,
  };
}

function buildBoardingPetSource(raw: any) {
  const nested = pickValue(raw, [
    "pet",
    "occupant_pet",
    "active_boarding.pet",
    "boarding.pet",
  ], null);

  // On the Pet Boarding doctype `pet` is a Link → its value IS the pet id (e.g. "PET-00001").
  // When the backend returns that link as a plain STRING (not an expanded object), wrap it as the
  // record id and graft on the flat display name/image so `mapPetSummary` resolves both id + label.
  const nestedValue = nested as unknown;
  if (typeof nestedValue === "string" && nestedValue.trim()) {
    return {
      name: nestedValue.trim(),
      pet_name: pickValue(raw, ["pet_name", "custom_pet_name", "pet_id.pet_name"], null),
      pet_image: pickValue(raw, ["pet_image", "custom_pet_image"], null),
      animal_species: pickValue(raw, ["animal_species", "pet_species"], null),
      breed: pickValue(raw, ["breed", "pet_breed"], null),
      gender: pickValue(raw, ["gender", "pet_gender"], null),
    };
  }

  if (nested)
    return nested;

  const linkId = typeof nested === "string" ? nested : null;

  return {
    pet_id: pickValue(raw, [
      "pet_id",
      "custom_pet_id",
      "active_boarding.pet_id",
      "boarding.pet_id",
    ], null) ?? linkId,
    pet_name: pickValue(raw, [
      "pet_name",
      "custom_pet_name",
      "pet_id.pet_name",
      "active_boarding.pet_name",
      "boarding.pet_name",
      "active_boarding.pet_id.pet_name",
      "boarding.pet_id.pet_name",
    ], null),
    pet_image: pickValue(raw, [
      "pet_image",
      "custom_pet_image",
      "pet_id.pet_image",
      "active_boarding.pet_image",
      "boarding.pet_image",
      "active_boarding.pet_id.pet_image",
      "boarding.pet_id.pet_image",
    ], null),
    animal_species: pickValue(raw, ["animal_species", "pet_species"], null),
    breed: pickValue(raw, ["breed", "pet_breed"], null),
    gender: pickValue(raw, ["gender", "pet_gender"], null),
    age: pickValue(raw, ["age", "pet_age"], null),
    weight: pickValue(raw, ["weight", "pet_weight"], null),
  };
}

function buildBoardingGuardianSource(raw: any) {
  const nested = pickValue(raw, [
    "guardian",
    "customer",
    "occupant_guardian",
    "active_boarding.guardian",
    "active_boarding.customer",
    "boarding.guardian",
    "boarding.customer",
  ], null);

  // A nested object is the richest source. When the link comes back as a bare id string
  // (Frappe link field), the summary mapper can't read an id out of a string — keep it as the
  // id and fall through to the flat fields for name/image/etc.
  if (nested && typeof nested === "object")
    return nested;

  const linkId = typeof nested === "string" ? nested : null;

  return {
    guardian_id: pickValue(raw, [
      "guardian_id",
      "guardian_id",
      "custom_guardian_id",
      "customer_id",
      "active_boarding.guardian_id",
      "active_boarding.guardian_id",
      "active_boarding.customer_id",
      "boarding.guardian_id",
      "boarding.guardian_id",
      "boarding.customer_id",
    ], null) ?? linkId,
    guardian_name: pickValue(raw, [
      "guardian_name",
      "guardian_name",
      "customer_name",
      "owner_name",
      "full_name",
      "guardian_id.full_name",
      "guardian_id.full_name",
      "active_boarding.guardian_name",
      "active_boarding.guardian_name",
      "active_boarding.customer_name",
      "active_boarding.owner_name",
      "active_boarding.guardian_id.full_name",
      "active_boarding.guardian_id.full_name",
      "boarding.guardian_name",
      "boarding.guardian_name",
      "boarding.customer_name",
      "boarding.owner_name",
      "boarding.guardian_id.full_name",
      "boarding.guardian_id.full_name",
    ], null),
    guardian_image: pickValue(raw, [
      "guardian_image",
      "guardian_image",
      "customer_image",
      "guardian_id.guardian_image",
      "guardian_id.guardian_image",
      "active_boarding.guardian_image",
      "active_boarding.guardian_image",
      "active_boarding.customer_image",
      "active_boarding.guardian_id.guardian_image",
      "active_boarding.guardian_id.guardian_image",
      "boarding.guardian_image",
      "boarding.guardian_image",
      "boarding.customer_image",
      "boarding.guardian_id.guardian_image",
      "boarding.guardian_id.guardian_image",
    ], null),
    mobile_no: pickValue(raw, [
      "mobile_no",
      "phone",
      "phone_number",
      "contact_number",
      "guardian_id.mobile_no",
      "guardian_id.mobile_no",
    ], null),
    email_id: pickValue(raw, ["email_id", "email", "guardian_id.email_id", "guardian_id.email_id"], null),
  };
}

/**
 * Backend-owned action flags for a boarding record. We pass the flag through as-is (never derive
 * it): `can_cancel_boarding` gates the desk's cancel action exactly like the visit workbench's
 * `permissions`. Absent → null, so the action simply doesn't render (the always-emit-the-key rule
 * cuts the other way here — a missing key must fail closed, not guess a value).
 */
function mapBoardingPermissions(raw: any): BoardingPermissions | null {
  const source = pickValue<Record<string, unknown>>(
    raw,
    ["permissions", "boarding.permissions", "active_boarding.permissions"],
  );
  if (!source || typeof source !== "object")
    return null;

  return {
    can_cancel_boarding: source.can_cancel_boarding === true,
    can_give_medication: source.can_give_medication === true,
    can_dispense_medication: source.can_dispense_medication === true,
  };
}

/**
 * One Payment Entry row from a boarding response.
 *
 * Returns `null` for anything without an id — a row that cannot be linked back to its Payment
 * Entry is not something to show a desk user, who would have no way to check it.
 */
export function mapBoardingPaymentRow(raw: any): BoardingPaymentRow | null {
  const id = String(raw?.name ?? raw?.id ?? raw?.payment_entry ?? "").trim();

  if (!id)
    return null;

  return {
    id,
    postingDate: toNullableString(raw, ["posting_date", "date"]),
    paidAmount: toNumberValue(raw, ["paid_amount", "amount"], null) ?? null,
    unallocatedAmount: toNumberValue(raw, ["unallocated_amount"], null) ?? null,
    paidTo: toNullableString(raw, ["paid_to", "account"]),
    modeOfPayment: toNullableString(raw, ["mode_of_payment"]),
    owner: toNullableString(raw, ["owner", "recorded_by"]),
    remarks: toNullableString(raw, ["remarks", "remark", "note"]),
  };
}

/**
 * The payment history off any boarding response.
 *
 * ⚠️ An absent key and a non-array both yield `[]`, deliberately — same defensive stance as
 * `readSkippedPets`. Callers must therefore treat an empty list as "nothing to show" and HIDE
 * the history, never render it as "no payments have been made": the stay read may simply not
 * carry the key yet, and telling a guardian their instalments do not exist is far worse than
 * telling them nothing.
 */
export function readBoardingPayments(raw: any): BoardingPaymentRow[] {
  const rows = raw?.payments ?? raw?.payment_entries ?? raw?.paymentEntries ?? null;

  if (!Array.isArray(rows))
    return [];

  return rows
    .map(mapBoardingPaymentRow)
    .filter((row): row is BoardingPaymentRow => row !== null);
}

export function mapBoardingUnit(raw: any): BoardingUnitSummary {
  const recordStatus = normalizeBoardingRecordStatus(
    toNullableString(raw, [
      "record_status",
      "boarding_status",
      "boarding.record_status",
      "active_boarding.record_status",
      "active_boarding.boarding_status",
      "status",
    ]),
  );

  const hasPetData = Boolean(
    pickValue(raw, [
      "pet",
      "occupant_pet",
      "active_boarding.pet",
      "boarding.pet",
      "pet_id",
      "active_boarding.pet_id",
      "boarding.pet_id",
      "pet.name",
      "occupant_pet.name",
      "pet_name",
      "active_boarding.pet_name",
      "boarding.pet_name",
      "custom_pet_id",
      "custom_pet_name",
      "pet.pet_name",
      "occupant_pet.pet_name",
      "active_boarding.pet.pet_name",
      "boarding.pet.pet_name",
    ]),
  );

  const hasGuardianData = Boolean(
    pickValue(raw, [
      "guardian",
      "customer",
      "occupant_guardian",
      "active_boarding.guardian",
      "active_boarding.customer",
      "boarding.guardian",
      "boarding.customer",
      "guardian_id",
      "guardian_id",
      "customer_id",
      "guardian.name",
      "customer.name",
      "occupant_guardian.name",
      "guardian_name",
      "guardian_name",
      "customer_name",
      "owner_name",
      "full_name",
      "active_boarding.guardian_name",
      "active_boarding.guardian_name",
      "active_boarding.customer_name",
      "active_boarding.owner_name",
      "boarding.guardian_name",
      "boarding.guardian_name",
      "boarding.customer_name",
      "boarding.owner_name",
    ]),
  );

  const petSource = buildBoardingPetSource(raw);
  const guardianSource = buildBoardingGuardianSource(raw);
  const pet = hasPetData ? mapPetSummary(petSource) : null;
  const guardian = hasGuardianData ? mapGuardianSummary(guardianSource) : null;

  return {
    // Deliberately unchanged and room-first: `id` is the Vue key / `patchUnit` match key and the
    // tolerant `name:` read argument. It is @deprecated for ACTIONS — the two branded ids below
    // are what record and room work must use (docs/BOARDING_GAPS.md Tier-1 #1).
    id: toStringValue(raw, ["name", "room_id", "service_room_id", "boarding_id", "id"]),
    boardingRecordId: asBoardingRecordId(
      toNullableString(raw, ["boarding_record_id", "boarding_id", "record_id", "active_boarding_id"]),
    ),
    permissions: mapBoardingPermissions(raw),
    unitCode: toStringValue(raw, ["unit_code", "cage_no", "cage_code", "name"]),
    unitLabel: toNullableString(raw, ["unit_label", "room_name", "kennel_name", "title"]),
    roomId: asRoomId(toNullableString(raw, ["room_id", "service_room_id", "room.name"])),
    roomImage: toNullableString(raw, ["room_image", "image", "room.image"]),
    roomStatus: toNullableString(raw, ["room_status", "service_room_status"]),
    roomType: toNullableString(raw, ["room_type", "unit_type", "cage_type"]),
    status: normalizeBoardingOccupancyStatus(
      toNullableString(raw, [
        "occupancy_status",
        "room_occupancy_status",
        "status",
        "boarding.status",
        "active_boarding.status",
      ]),
      recordStatus,
    ),
    recordStatus,
    boardingType: toNullableString(raw, [
      "boarding_type",
      "boardingType",
      "type",
      "active_boarding.boarding_type",
      "boarding.boarding_type",
    ]),
    petName: pet?.name || toNullableString(raw, [
      "pet_name",
      "custom_pet_name",
      "active_boarding.pet_name",
      "boarding.pet_name",
    ]),
    guardianName: guardian?.name || toNullableString(raw, [
      "guardian_name",
      "guardian_name",
      "customer_name",
      "owner_name",
      "active_boarding.guardian_name",
      "active_boarding.guardian_name",
      "active_boarding.customer_name",
      "active_boarding.owner_name",
      "boarding.guardian_name",
      "boarding.guardian_name",
      "boarding.customer_name",
      "boarding.owner_name",
    ]),
    pet,
    guardian,
    reservedAt: toNullableString(raw, ["reserved_at", "reservation_time", "created_at", "creation"]),
    checkIn: toNullableString(raw, ["check_in", "check_in_date", "arrival_date"]),
    checkOut: toNullableString(raw, ["check_out", "check_out_date", "departure_date"]),
    // THIS ROOM's occupants, not the booking's — `list_boarding_units` is occupant-grained, so a
    // booking split across two rooms appears on both cards with only its own animals on each.
    // `active_occupant_count` is likewise the ROOM's; putting a booking-level count on a card
    // would double-count a split booking.
    // Top level of the card, and nowhere else (Part D §7). `active_boarding` is a FLAT booking
    // record with no child tables, and a hub card has no `boarding` key at all — both guessed
    // prefixes were dead. `mapBoardingDetail` overrides these with the booking grain's own
    // lookup, which legitimately reads `boarding.occupants`.
    occupants: (pickValue<any[]>(raw, ["occupants"], []) || []).map(mapBoardingOccupant),
    // Confirmed THIS ROOM's headcount: a booking split across two rooms reads e.g. 5 and 2 on
    // the two cards, never 7 and 7.
    activeOccupantCount: toNumberValue(raw, ["active_occupant_count"], null) ?? null,
    /**
     * ⚠️ FIELD NAME UNVERIFIED. `Service Room.capacity` is not in the contract — every "capacity"
     * in it refers to `max_pets_per_booking`, the booking policy — so these aliases are inferred
     * from the described behaviour, not read from a spec. Whichever one the backend actually
     * uses, only this list changes.
     *
     * All five are semantically specific, so none can match the WRONG thing the way an `image`
     * alias once matched a room photo. `max_pets` cannot collide with `max_pets_per_booking`:
     * lookup is exact-key then dotted-path, never prefix.
     *
     * Also tried nested, because the detail's room data arrives in a room block.
     */
    capacity: toNumberValue(raw, [
      "capacity",
      "room_capacity",
      "max_occupants",
      "max_pets",
      "pet_capacity",
      "room.capacity",
      "service_room.capacity",
    ], null) ?? null,
    // Element shape is undocumented (§5), so accept a name or an object carrying one and drop
    // anything unreadable. Nothing depends on this: attribution comes from `occupants[].boarding`
    // and the count from `booking_count`, both of which ARE documented.
    bookings: (pickValue<any[]>(raw, ["bookings"], []) || [])
      .map(entry => typeof entry === "string"
        ? entry.trim()
        : String(entry?.name ?? entry?.boarding_id ?? entry?.boarding ?? "").trim())
      .filter(Boolean),
    bookingCount: toNumberValue(raw, ["booking_count"], null) ?? null,
    stayDays: toNumberValue(raw, ["stay_days", "number_of_days"], null) ?? null,
    stayHours: toStayHours(raw),
    totalCost: toNumberValue(raw, [
      "total_cost",
      "total_amount",
      "grand_total",
      "active_boarding.total_cost",
      "active_boarding.total_amount",
      "active_boarding.grand_total",
      "boarding.total_cost",
      "boarding.total_amount",
      "boarding.grand_total",
    ], null) ?? null,
    deposit: toNumberValue(raw, [
      "deposit",
      "advance_amount",
      "paid_amount",
      "deposit_amount",
      "active_boarding.deposit",
      "active_boarding.advance_amount",
      "active_boarding.paid_amount",
      "active_boarding.deposit_amount",
      "boarding.deposit",
      "boarding.advance_amount",
      "boarding.paid_amount",
      "boarding.deposit_amount",
    ], null) ?? null,
    depositPaymentEntry: toNullableString(raw, [
      "deposit_payment_entry",
      "deposit_payment_entry_id",
      "payment_entry",
      "payment_entry_id",
      "payment_entry_name",
      "active_boarding.deposit_payment_entry",
      "active_boarding.deposit_payment_entry_id",
      "active_boarding.payment_entry",
      "active_boarding.payment_entry_id",
      "active_boarding.payment_entry_name",
      "boarding.deposit_payment_entry",
      "boarding.deposit_payment_entry_id",
      "boarding.payment_entry",
      "boarding.payment_entry_id",
      "boarding.payment_entry_name",
    ]),
    balance: toNumberValue(raw, [
      "balance",
      "balance_amount",
      "outstanding_amount",
      "active_boarding.balance",
      "active_boarding.balance_amount",
      "active_boarding.outstanding_amount",
      "boarding.balance",
      "boarding.balance_amount",
      "boarding.outstanding_amount",
    ], null) ?? null,
    /**
     * ⚠️ DELIBERATELY NOT ALIASED TO `paid_amount` OR `outstanding_amount`.
     *
     * Those two are already claimed above — `paid_amount` by `deposit` and
     * `outstanding_amount` by `balance`. Adding them here would make a guardian's third
     * instalment read as their deposit, and the invoice-level outstanding read as the
     * stay-level estimate that `balanceEstimateHint` explicitly disclaims. Only the exact
     * keys, so absent stays absent.
     */
    totalPaid: toNumberValue(raw, [
      "total_paid",
      "active_boarding.total_paid",
      "boarding.total_paid",
    ], null) ?? null,
    invoiceOutstanding: toNumberValue(raw, [
      "invoice_outstanding",
      "active_boarding.invoice_outstanding",
      "boarding.invoice_outstanding",
    ], null) ?? null,
    salesInvoice: toNullableString(raw, [
      "sales_invoice",
      "active_boarding.sales_invoice",
      "boarding.sales_invoice",
    ]),
    payments: readBoardingPayments(raw)
      .concat(readBoardingPayments(raw?.active_boarding))
      .concat(readBoardingPayments(raw?.boarding))
      .filter((row, index, all) => all.findIndex(other => other.id === row.id) === index),
    // The RESERVATION note, unchanged. It is its own field and is not the check-in note.
    note: toNullableString(raw, ["note", "special_instructions"]),
    /**
     * The stay's CHECK-IN note. `check_in_note` is the Pet Boarding fieldname and is read FIRST.
     *
     * Nothing here read it before, which is why a note typed at check-in came back blank on the
     * detail page AND left `BoardingWorkflowDialog`'s prefill empty — the note was saved and then
     * unreachable, so it read as "not linked".
     *
     * ⚠️ Bare `notes` is deliberately NOT in this list. On the unit/room grain `notes` is SERVICE
     * ROOM master data (contract §"room master data"; the field is also modelled as
     * `ServiceRoom.notes`), so reading it here labelled the kennel's own description as this
     * stay's check-in note. The plural spellings that remain are tolerance only — both are
     * unambiguously check-in-scoped and cannot collide with room data.
     */
    notes: toNullableString(raw, [
      "check_in_note",
      "check_in_notes",
      "checkin_notes",
      // NESTED, for the same reason `balance` and `visit` above carry these prefixes: this
      // response does not put every booking field at the top level. Not invented — it is the
      // established shape in this very mapper.
      "boarding.check_in_note",
      "active_boarding.check_in_note",
    ]),
    /** Same rule for the departure note: `check_out_note` is the fieldname, the rest is spelling tolerance. */
    checkoutNotes: toNullableString(raw, [
      "check_out_note",
      "checkout_notes",
      "checkout_note",
      "boarding.check_out_note",
      "active_boarding.check_out_note",
    ]),
  };
}

/**
 * One roster row. Field names come straight off the `Pet Boarding Occupant` child table, so the
 * aliases here are only camel/snake tolerance — not invented fallbacks.
 */
export function mapBoardingOccupant(raw: any): BoardingOccupant {
  return {
    name: toNullableString(raw, ["name"]),
    pet: String(pickValue<string>(raw, ["pet", "pet_id"], "") || ""),
    petName: toNullableString(raw, ["pet_name", "petName"]),
    status: (toNullableString(raw, ["status"]) || "Active") as BoardingOccupantStatus,
    boardingType: toNullableString(raw, ["boarding_type", "boardingType"]),
    serviceRoom: toNullableString(raw, ["service_room", "serviceRoom", "room_id"]),
    joinedAt: toNullableString(raw, ["joined_at", "joinedAt"]),
    departedAt: toNullableString(raw, ["departed_at", "departedAt"]),
    deathRecord: toNullableString(raw, ["death_record", "deathRecord"]),
    departureNote: toNullableString(raw, ["departure_note", "departureNote"]),
    // Which booking this pet belongs to. Required for a two-booking room, where the card's flat
    // `boarding_id` names only the primary (Part D §5).
    boarding: toNullableString(raw, ["boarding"]),
    /**
     * The photo. `pet_image` is the agreed name and the ONLY alias — verified against Part D §7.
     *
     * **Shipped** (§3.1) — on the occupant row in both grains. Nullable and often null overall
     * (23% of all pets), but 19 of the 24 currently boarded carry a photo (79%), so the roster
     * mostly draws real faces. A null is the cue to draw the glyph fallback, not a fault.
     *
     * ⚠️ DELETED ALIASES, both actively harmful — do not restore them:
     *  • `image` — the SERVICE ROOM's photo on a hub card. It would put a picture of a kennel
     *    in an avatar slot.
     *  • `animal_species` — taxonomic CLASS, not species: 2,216 of 2,224 pets read `Mammal`.
     *    It cannot tell a cat from a dog and would render one glyph for 99.6% of animals.
     *  • `petImage` / `animalType` camelCase — nothing in this API is camelCase.
     *  • `active_boarding.pet_image` — the key exists but is permanently null on this endpoint;
     *    the hub's query does not select it.
     */
    petImage: toNullableString(raw, ["pet_image"]),
    // Shipping in the same round as `pet_image`. 100% populated on Pet (Cat 1,788 · Dog 424 ·
    // Bird 8 · Rabbit 3 · Horse 1) and already the axis boarding rates resolve on, so it is a
    // maintained field. `animal_species` is deliberately NOT an alias — see the note above.
    animalType: toNullableString(raw, ["animal_type"]),
    /**
     * The server's per-pet daily rate: this animal's species and boarding type, or its
     * staff-corrected room row. `undefined` = key absent (older backend / hub row);
     * `null` = the catalogue cannot price it. Never coerce either to 0.
     */
    dailyRate: raw && "daily_rate" in raw
      ? (raw.daily_rate === null || !Number.isFinite(Number(raw.daily_rate)) ? null : Number(raw.daily_rate))
      : undefined,
    dailyRateItem: toNullableString(raw, ["daily_rate_item"]),
  };
}

export function mapBoardingDetail(raw: any): BoardingDetail {
  const base = mapBoardingUnit(raw);

  return {
    ...base,
    ...readBoardingAccommodationTotals(raw),
    boardingDiscountSupported: raw?.capabilities?.boarding_discount_v1 === true,
    billingStatus: toNullableString(raw, [
      "billing_status",
      "payment_status",
    ]) as BillingStatus | null,
    billableItems: (pickValue<any[]>(raw, ["billable_items", "items", "services"], []) || [])
      .map(mapBillableItem),
    // The roster. Read from `occupants` (or the nested `boarding.occupants` the write endpoints
    // return). NOT from `pet`/`pet_id`/`pet_name`, which name only the first pet reserved, and
    // NOT from `occupancy`, which is a room-availability string (contract §1.0, §1.1).
    occupants: (pickValue<any[]>(raw, ["occupants", "boarding.occupants"], []) || [])
      .map(mapBoardingOccupant),
    activeOccupantCount: toNumberValue(raw, [
      "active_occupant_count",
      "boarding.active_occupant_count",
    ], null) ?? null,
    /**
     * THE ROOM GRAIN — `get_boarding_detail` returns BOTH, and they answer different questions
     * (contract §1.1a):
     *
     *   `occupants`      → **booking**: every pet on this booking, wherever it sleeps
     *   `room.occupants` → **room**:    every pet in this booking's room, WHOEVER'S BOOKING
     *
     * ⚠️ **This is the only list that can see another booking's pets.** A room may hold two
     * bookings (Part D §5), and the booking grain above — correctly — hides them. Anything that
     * needs to know what is physically in the kennel reads THIS.
     *
     * ⚠️ **Never render the stay's roster from it**, and never act on it without narrowing by
     * `boarding` first: every occupant endpoint takes exactly one booking.
     */
    roomOccupants: (pickValue<any[]>(raw, ["room.occupants"], []) || [])
      .map(mapBoardingOccupant),
    roomActiveOccupantCount: toNumberValue(raw, ["room.active_occupant_count"], null) ?? null,
    // Backlink to the originating Vet Visit (visit-origin boarding). Display-only.
    visit: toNullableString(raw, [
      "visit",
      "vet_visit",
      "source_visit",
      "active_boarding.visit",
      "boarding.visit",
    ]),
  };
}

export function mapBoardingRecord(raw: any): BoardingRecordListItem {
  const recordStatus = normalizeBoardingRecordStatus(
    toNullableString(raw, [
      "record_status",
      "boarding_status",
      "status",
    ]),
  );

  const hasPetData = Boolean(
    pickValue(raw, [
      "pet",
      "occupant_pet",
      "active_boarding.pet",
      "boarding.pet",
      "pet_id",
      "active_boarding.pet_id",
      "boarding.pet_id",
      "pet.name",
      "occupant_pet.name",
      "pet_name",
      "active_boarding.pet_name",
      "boarding.pet_name",
      "custom_pet_id",
      "custom_pet_name",
      "pet.pet_name",
      "occupant_pet.pet_name",
      "active_boarding.pet.pet_name",
      "boarding.pet.pet_name",
    ]),
  );

  const hasGuardianData = Boolean(
    pickValue(raw, [
      "guardian",
      "customer",
      "occupant_guardian",
      "active_boarding.guardian",
      "active_boarding.customer",
      "boarding.guardian",
      "boarding.customer",
      "guardian_id",
      "guardian_id",
      "customer_id",
      "guardian.name",
      "customer.name",
      "occupant_guardian.name",
      "guardian_name",
      "guardian_name",
      "customer_name",
      "owner_name",
      "full_name",
      "active_boarding.guardian_name",
      "active_boarding.guardian_name",
      "active_boarding.customer_name",
      "active_boarding.owner_name",
      "boarding.guardian_name",
      "boarding.guardian_name",
      "boarding.customer_name",
      "boarding.owner_name",
    ]),
  );

  const petSource = buildBoardingPetSource(raw);
  const guardianSource = buildBoardingGuardianSource(raw);
  const pet = hasPetData ? mapPetSummary(petSource) : null;
  const guardian = hasGuardianData ? mapGuardianSummary(guardianSource) : null;

  return {
    // These rows ARE `Pet Boarding` docs, so `id` is unambiguously a boarding record id.
    id: asBoardingRecordId(toStringValue(raw, ["name", "boarding_id", "record_id", "id"]))!,
    permissions: mapBoardingPermissions(raw),
    roomId: asRoomId(toNullableString(raw, ["room_id", "service_room_id"])),
    roomCode: toStringValue(raw, ["unit_code", "room_code", "cage_code", "name"]),
    roomName: toNullableString(raw, ["unit_label", "room_name", "title"]),
    roomType: toNullableString(raw, ["room_type", "unit_type"]),
    roomImage: toNullableString(raw, ["room_image", "image"]),
    occupancyStatus: normalizeBoardingOccupancyStatus(
      toNullableString(raw, ["occupancy_status", "room_occupancy_status", "status"]),
      recordStatus,
    ),
    recordStatus,
    boardingType: toNullableString(raw, [
      "boarding_type",
      "boardingType",
      "type",
      "active_boarding.boarding_type",
      "boarding.boarding_type",
    ]),
    petName: pet?.name || toNullableString(raw, [
      "pet_name",
      "custom_pet_name",
      "active_boarding.pet_name",
      "boarding.pet_name",
    ]),
    guardianName: guardian?.name || toNullableString(raw, [
      "guardian_name",
      "guardian_name",
      "customer_name",
      "owner_name",
      "active_boarding.guardian_name",
      "active_boarding.guardian_name",
      "active_boarding.customer_name",
      "active_boarding.owner_name",
      "boarding.guardian_name",
      "boarding.guardian_name",
      "boarding.customer_name",
      "boarding.owner_name",
    ]),
    pet,
    guardian,
    reservedAt: toNullableString(raw, ["reserved_at", "reservation_time", "created_at", "creation"]),
    checkIn: toNullableString(raw, ["check_in", "check_in_date", "arrival_date"]),
    checkOut: toNullableString(raw, ["check_out", "check_out_date", "departure_date"]),
    stayDays: toNumberValue(raw, ["stay_days", "number_of_days"], null) ?? null,
    stayHours: toStayHours(raw),
    totalCost: toNumberValue(raw, [
      "total_cost",
      "total_amount",
      "grand_total",
      "active_boarding.total_cost",
      "active_boarding.total_amount",
      "active_boarding.grand_total",
      "boarding.total_cost",
      "boarding.total_amount",
      "boarding.grand_total",
    ], null) ?? null,
    deposit: toNumberValue(raw, [
      "deposit",
      "advance_amount",
      "paid_amount",
      "deposit_amount",
      "active_boarding.deposit",
      "active_boarding.advance_amount",
      "active_boarding.paid_amount",
      "active_boarding.deposit_amount",
      "boarding.deposit",
      "boarding.advance_amount",
      "boarding.paid_amount",
      "boarding.deposit_amount",
    ], null) ?? null,
    depositPaymentEntry: toNullableString(raw, [
      "deposit_payment_entry",
      "deposit_payment_entry_id",
      "payment_entry",
      "payment_entry_id",
      "payment_entry_name",
      "active_boarding.deposit_payment_entry",
      "active_boarding.deposit_payment_entry_id",
      "active_boarding.payment_entry",
      "active_boarding.payment_entry_id",
      "active_boarding.payment_entry_name",
      "boarding.deposit_payment_entry",
      "boarding.deposit_payment_entry_id",
      "boarding.payment_entry",
      "boarding.payment_entry_id",
      "boarding.payment_entry_name",
    ]),
    balance: toNumberValue(raw, [
      "balance",
      "balance_amount",
      "outstanding_amount",
      "active_boarding.balance",
      "active_boarding.balance_amount",
      "active_boarding.outstanding_amount",
      "boarding.balance",
      "boarding.balance_amount",
      "boarding.outstanding_amount",
    ], null) ?? null,
    // See `mapBoardingUnit` for why these are read from their exact keys only and never
    // folded into `deposit` / `balance` above.
    totalPaid: toNumberValue(raw, [
      "total_paid",
      "active_boarding.total_paid",
      "boarding.total_paid",
    ], null) ?? null,
    invoiceOutstanding: toNumberValue(raw, [
      "invoice_outstanding",
      "active_boarding.invoice_outstanding",
      "boarding.invoice_outstanding",
    ], null) ?? null,
    salesInvoice: toNullableString(raw, [
      "sales_invoice",
      "active_boarding.sales_invoice",
      "boarding.sales_invoice",
    ]),
    payments: readBoardingPayments(raw)
      .concat(readBoardingPayments(raw?.active_boarding))
      .concat(readBoardingPayments(raw?.boarding))
      .filter((row, index, all) => all.findIndex(other => other.id === row.id) === index),
    billingStatus: toNullableString(raw, ["billing_status", "payment_status"]) as BillingStatus | null,
    note: toNullableString(raw, ["note", "special_instructions"]),
    // Same alias order as `mapBoardingUnit` — see the note there for why bare `notes` is absent.
    notes: toNullableString(raw, [
      "check_in_note",
      "check_in_notes",
      "checkin_notes",
      "boarding.check_in_note",
      "active_boarding.check_in_note",
    ]),
    checkoutNotes: toNullableString(raw, [
      "check_out_note",
      "checkout_notes",
      "checkout_note",
      "boarding.check_out_note",
      "active_boarding.check_out_note",
    ]),
    billableItems: (pickValue<any[]>(raw, ["billable_items", "items", "services"], []) || [])
      .map(mapBillableItem),
  };
}
